/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './SupplierTypeTable.scss';
import { mainColors } from '../../helpers/colors';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, cilPencil, cilTrash } from '@coreui/icons';
import { deleteSupplierTypeRequestAction } from '../../store/supplierType/supplierTypeActions';
import { useTranslation } from '../../hooks/useTranslation';
import { useResizableColumns } from '../../hooks/useResizableColumns';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  }
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

function SupplierTypeTable(props) {
  const {
    supplierTypeList,
    deleteSupplierTypeRequest
  } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index','SupplierTypeName','SupplierTypeDescription','actions'], 'SupplierTypeTable');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  const columns = [
    { id: 'index', label: '#', sortable: false },
    { id: 'SupplierTypeName', label: t('supplierTypeName'), sortable: true },
    { id: 'SupplierTypeDescription', label: t('Description'), sortable: true },
    { id: 'actions', label: t('Actions'), sortable: false }
  ];

  useEffect(() => {
    if (supplierTypeList && Array.isArray(supplierTypeList)) {
      setTotalRows(supplierTypeList.length);
      setTableData(supplierTypeList);
    }
  }, [supplierTypeList]);

  const handleEdit = (item) => {
    navigate(`/suppliertype/edit/${item.SupplierTypeId}`, {
      state: { supplierType: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const sessionUserId = localStorage.getItem('sessionUserId');
      if (sessionUserId && sessionUserId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(sessionUserId),
          SupplierTypeId: deleteModal.item.SupplierTypeId
        };
        deleteSupplierTypeRequest({ data: deleteData });
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const getSortedData = () => {
    const sorted = stableSort(tableData, getComparator(order, orderBy));
    const start = (page - 1) * rowsPerPage;
    const end = page * rowsPerPage;
    return sorted.slice(start, end);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <CPaginationItem 
        key={i} 
        active={i === page} 
        onClick={() => handleChangePage(i)}
      >
        {i}
      </CPaginationItem>
    );
  }

  return (
    <div className="suppliertype-table-container">
      <CCard className="mb-4">
        <CCardBody>
          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell 
                      key={column.id}
                      className={`table-header-cell ${column.sortable ? 'sortable-header' : ''}`}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{ cursor: column.sortable ? 'pointer' : 'default', fontStyle: 'normal', paddingRight: '8px', ...(headerProps.style || {}) }}
                    >
                      {column.label}
                      {column.sortable && orderBy === column.id && (
                        <CIcon
                          icon={order === 'asc' ? cilArrowTop : cilArrowBottom}
                          size="sm"
                          className="ms-1"
                        />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <CTableRow key={item.SupplierTypeId || index}>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      {(page - 1) * rowsPerPage + index + 1}
                    </CTableDataCell>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      <CBadge color="primary" shape="rounded-pill">
                        {item.SupplierTypeName}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      <span title={item.SupplierTypeDescription} style={{ fontStyle: 'normal' }}>
                        {item.SupplierTypeDescription?.length > 50 ? `${item.SupplierTypeDescription.substring(0, 50)}...` : item.SupplierTypeDescription}
                      </span>
                    </CTableDataCell>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('edit')}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleEdit(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilPencil}
                              size="sm"
                              style={{ color: mainColors.primary }}
                            />
                          </div>
                        </CTooltip>
                        
                        <CTooltip content={t('delete')}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleDeleteClick(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilTrash}
                              size="sm"
                              style={{ color: '#dc3545' }}
                            />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={4} className="text-center py-4" style={{ fontStyle: 'normal' }}>
                    <div className="text-muted" style={{ fontStyle: 'normal' }}>
                      {t('loadingData')}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>

          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontStyle: 'normal' }}>{t('Show')}:</span>
                <select 
                  className="form-select form-select-sm" 
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(e)}
                  style={{ width: 'auto', fontStyle: 'normal' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted" style={{ fontStyle: 'normal' }}>{t('entries')}</span>
              </div>
              
              <CPagination>
                <CPaginationItem 
                  aria-label={t('Previous')} 
                  disabled={page === 1}
                  onClick={() => handleChangePage(page - 1)}
                >
                  <span aria-hidden="true">&laquo;</span>
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem 
                  aria-label={t('Next')} 
                  disabled={page === totalPages}
                  onClick={() => handleChangePage(page + 1)}
                >
                  <span aria-hidden="true">&raquo;</span>
                </CPaginationItem>
              </CPagination>
              
              <div className="text-muted" style={{ fontStyle: 'normal' }}>
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle style={{ fontStyle: 'normal' }}>{t('Confirm Delete')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p style={{ fontStyle: 'normal' }}>{t('confirmDeleteMessage')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded" style={{ fontStyle: 'normal' }}>
              <strong>{t('supplierTypeName')}:</strong> {deleteModal.item.SupplierTypeName}<br/>
              <strong>{t('Description')}:</strong> {deleteModal.item.SupplierTypeDescription}<br/>
              <strong>{t('id')}:</strong> {deleteModal.item.SupplierTypeId}
            </div>
          )}
          <p className="text-danger mt-2 mb-0" style={{ fontStyle: 'normal' }}>
            <small>{t('This action cannot be undone.')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel} style={{ fontStyle: 'normal' }}>
            {t('Cancel')}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm} style={{ fontStyle: 'normal' }}>
            {t('Delete')}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  supplierTypeList: state.supplierTypeReducer?.supplierTypeList || []
});

const mapDispatchToProps = {
  deleteSupplierTypeRequest: deleteSupplierTypeRequestAction
};

export default connect(mapStateToProps, mapDispatchToProps)(SupplierTypeTable);


